import { createHttpClient } from '@adapters/HttpClient';
import { PreparationStatus } from '@modules/OrderPreparation/shared/config';
import { faBoxesStacked, faCartShopping, faLocationDot, faPrint } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { apiClientWH } from '@shared/services';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAttachmentPrinterScan, usePackingStationScan, usePrinterSelection, usePrintOrder } from '../../hooks';
import { useOrderSorting } from '../../hooks/useOrderSorting';
import { usePackingFlow } from '../../hooks/usePackingFlow';
import { usePreparationWithProducts } from '../../hooks/usePreparationWithProducts';
import { countOrderAttachments, orderHasAttachments } from '../../utils/orderAttachments';
import { packingScanCodesMatch } from '../../utils/packingAssignKey';
import {
  AttachmentPrinterRequiredAlert,
  Breadcrumb,
  OrderProductsList,
  OrdersList,
  OrderSortControls,
  PackingStationRequiredAlert,
  PrinterRequiredAlert,
  ProductsList,
} from '../display';
import { ScannerInput } from '../interaction';
import { ChangePackagesModal } from '../modals';

export const Main: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('packingModule');
  const preparationId = id ? parseInt(id, 10) : 0;
  const changePackagesModalRef = useRef<ModalCenterRef>(null);

  const {
    preparation,
    orders,
    packagings,
    loading,
    ordersLoading,
    error,
    ordersError,
    isPreparationCompleted,
    refreshOrders,
  } = usePreparationWithProducts(preparationId);

  const { needsAttachmentPrinterScan, isAttachmentPrinterReady, attachmentPrinterCode, scanAttachmentPrinter } =
    useAttachmentPrinterScan(orders);

  const {
    needsPackingStationScan,
    isPackingStationReady,
    packingStation,
    scanPackingStation,
    isPackingStationLoading,
  } = usePackingStationScan(orders, packagings);

  const {
    isPackingMode,
    selectedOrder,
    assignedQuantities,
    assignedPackagingQuantities,
    assignedPackagingScanDetails,
    completedOrder,
    isSendingOrder,
    processScannedCode,
    exitPackingMode,
    activeBatchIdByProduct,
    setActiveBatchIdForProduct,
  } = usePackingFlow(preparation, orders, refreshOrders, packagings, attachmentPrinterCode, packingStation);

  const { printOrder, isOrderLoading } = usePrintOrder();

  const { hasPrinterSelected } = usePrinterSelection();

  const { sortConfig, setSortConfig } = useOrderSorting();

  const [cart, setCart] = useState<{ data: { code: string } } | null>(null);
  const [isCartScanned, setCartScanned] = useState<boolean>(false);

  useEffect(() => {
    if (!preparation?.location_id || isCartScanned || isPreparationCompleted) return;
    const client = createHttpClient(apiClientWH);
    client
      .get<{ data: { code: string } }>(`/carts/${preparation.location_id}`)
      .then((response) => setCart(response))
      .catch(() => {});
  }, [preparation, isCartScanned, isPreparationCompleted]);

  const handleScan = useCallback(
    async (code: string) => {
      if (needsPackingStationScan && !isPackingStationReady) {
        if (isPackingStationLoading) {
          return { success: false, message: 'Cargando puesto de trabajo...' };
        }
        return scanPackingStation(code);
      }

      if (!isCartScanned) {
        if (!cart) {
          return { success: false, message: 'Cargando carrito...' };
        }
        if (packingScanCodesMatch(code, cart.data.code)) {
          setCartScanned(true);
          return { success: true, message: 'Carrito confirmado' };
        }
        return { success: false, message: 'Carrito incorrecto' };
      }

      if (needsAttachmentPrinterScan && !isAttachmentPrinterReady) {
        return scanAttachmentPrinter(code);
      }

      return processScannedCode(code);
    },
    [
      needsPackingStationScan,
      isPackingStationReady,
      isPackingStationLoading,
      scanPackingStation,
      isCartScanned,
      cart,
      needsAttachmentPrinterScan,
      isAttachmentPrinterReady,
      scanAttachmentPrinter,
      processScannedCode,
    ],
  );

  const getStepDescription = () => {
    if (!hasPrinterSelected) {
      return 'Selecciona una impresora desde el menú desplegable superior para continuar';
    }
    if (needsPackingStationScan && !isPackingStationReady) {
      return 'Escanea el código del puesto de packing para continuar';
    }
    if (needsAttachmentPrinterScan && !isAttachmentPrinterReady) {
      return 'Escanea la impresora de folios para imprimir adjuntos';
    }
    if (isPreparationCompleted) {
      return 'Preparación completada - No se pueden escanear más productos';
    }
    if (isSendingOrder) {
      return 'Enviando datos del pedido - Espera un momento...';
    }
    if (isPackingMode && selectedOrder) {
      return `Escanea productos del pedido ${selectedOrder.ref}`;
    }
    return 'Escanea un producto para seleccionar un pedido';
  };

  const getDisabledReason = (): 'completed' | 'sending' | 'no-printer' | undefined => {
    if (!hasPrinterSelected) return 'no-printer';
    if (isPreparationCompleted) return 'completed';
    if (isSendingOrder) return 'sending';
    return undefined;
  };

  const scanStepDescription =
    !isPackingStationReady && needsPackingStationScan
      ? 'Escanea el puesto de trabajo'
      : !isCartScanned
        ? 'Escanea el carrito'
        : needsAttachmentPrinterScan && !isAttachmentPrinterReady
          ? 'Escanea la impresora de folios para imprimir adjuntos'
          : getStepDescription();

  const isScannerDisabled =
    isPreparationCompleted ||
    isSendingOrder ||
    !hasPrinterSelected ||
    (needsPackingStationScan && !isPackingStationReady && isPackingStationLoading) ||
    ((isPackingStationReady || !needsPackingStationScan) && !isCartScanned && !cart);

  const scanPhase =
    needsPackingStationScan && !isPackingStationReady
      ? 'packing-station'
      : !isCartScanned
        ? 'cart'
        : needsAttachmentPrinterScan && !isAttachmentPrinterReady
          ? 'attachment-printer'
          : 'product';

  const selectedOrderAttachmentCount = selectedOrder ? countOrderAttachments(selectedOrder) : 0;

  const bannerOrder = useMemo(() => {
    if (
      completedOrder &&
      (completedOrder.status_id === 6 || completedOrder.processed_at || completedOrder.shipment?.processed_at)
    ) {
      return completedOrder;
    }

    const completedOrders = (orders || []).filter(
      (order) => order.status_id === 6 || order.processed_at || order.shipment?.processed_at,
    );

    if (completedOrders.length === 0) {
      return null;
    }

    return completedOrders.sort((a, b) => {
      const dateA = new Date(a.shipment?.processed_at || a.processed_at || 0).getTime();
      const dateB = new Date(b.shipment?.processed_at || b.processed_at || 0).getTime();

      return dateB - dateA;
    })[0];
  }, [completedOrder, orders]);

  const isCompletedPreparation = preparation?.status === PreparationStatus.COMPLETE || isPreparationCompleted;

  // El estado COMPLETE de la preparacion debe mostrar el banner aunque la API no devuelva pedidos.
  // `completedOrder` vive solo en memoria; `bannerOrder` se reconstruye desde los pedidos de la API.
  const shouldShowCompletedBanner = Boolean(completedOrder || isCompletedPreparation);

  if (!preparationId || preparationId <= 0) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">ID de preparación inválido</h1>
          <p className="text-muted-foreground">Por favor, accede a una preparación válida usando la URL correcta.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando preparación...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">Error al cargar la preparación</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
        </div>
      </div>
    );
  }

  if (!preparation) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-muted-foreground mb-4">Preparación no encontrada</h1>
          <p className="text-muted-foreground">No se pudo encontrar la preparación con ID {preparationId}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl min-h-[calc(100vh-6rem)] flex flex-col">
      {/* Breadcrumb + Cart Badge */}
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <Breadcrumb preparationCode={preparation.code} preparationStatus={preparation.status} />
        {cart?.data?.code && (
          <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-800">
            <FontAwesomeIcon icon={faCartShopping} className="w-4 h-4" />
            <span className="font-medium text-sm">{cart.data.code}</span>
          </div>
        )}
        {isPackingStationReady && packingStation && needsPackingStationScan && (
          <div
            className="flex items-center space-x-2 bg-bg-warning text-warning px-3 py-2 rounded-lg border border-warning/30"
            data-testid="packing-station-badge"
          >
            <FontAwesomeIcon icon={faLocationDot} className="w-4 h-4" />
            <span className="font-medium text-sm">Puesto: {packingStation.code}</span>
          </div>
        )}
        {isAttachmentPrinterReady && attachmentPrinterCode && needsAttachmentPrinterScan && (
          <div
            className="flex items-center space-x-2 bg-bg-info text-info px-3 py-2 rounded-lg border border-info/30"
            data-testid="attachment-printer-badge"
          >
            <FontAwesomeIcon icon={faPrint} className="w-4 h-4" />
            <span className="font-medium text-sm">Folios: {attachmentPrinterCode}</span>
          </div>
        )}
      </div>

      {/* Alerta de impresora requerida */}
      {!hasPrinterSelected && <PrinterRequiredAlert />}

      {/* Alerta de puesto de packing requerido */}
      {hasPrinterSelected && needsPackingStationScan && !isPackingStationReady && !isPreparationCompleted && (
        <PackingStationRequiredAlert />
      )}

      {/* Alerta de impresora de folios requerida */}
      {hasPrinterSelected &&
        isPackingStationReady &&
        isCartScanned &&
        needsAttachmentPrinterScan &&
        !isAttachmentPrinterReady &&
        !isPreparationCompleted && <AttachmentPrinterRequiredAlert />}

      {/* Mensaje de pedido completado */}
      {shouldShowCompletedBanner && (
        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border border-green-200/50 dark:border-green-800/30 rounded-xl p-5 shadow-sm">
          {/* Mobile: columna (botones en fila nueva). Desktop lg+: fila horizontal sin cambios */}
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
            {/* Bloque izquierdo - Estado + info de pedido + courier (mobile: courier en fila aparte) */}
            <div className="flex flex-col gap-3 min-w-0 w-full lg:flex-row lg:items-center lg:gap-4">
              <div className="flex items-center space-x-4 min-w-0">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">
                      Completado
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {bannerOrder?.ref || preparation.code}
                  </h3>
                  {bannerOrder && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {bannerOrder.customer?.name} • {bannerOrder.shop?.name}
                    </p>
                  )}
                  {!bannerOrder && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Preparación completada</p>
                  )}
                </div>
              </div>
              {/* Courier - Mobile: fila propia debajo del pedido */}
              {bannerOrder?.shipment?.courier && (
                <div className="flex items-center space-x-2 pt-3 border-t border-green-200/50 dark:border-green-800/30 lg:pt-0 lg:border-t-0 lg:shrink-0">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-black/10 dark:bg-white/5 border border-white/10 flex items-center justify-center">
                    {bannerOrder.shipment.courier.image_hash ? (
                      <img
                        src={bannerOrder.shipment.courier.image_hash}
                        alt={bannerOrder.shipment.courier.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                        {bannerOrder.shipment.courier.name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-md font-bold text-gray-900 dark:text-white">
                      {bannerOrder.shipment.courier.name}
                    </span>
                    {bannerOrder.shipment.tracking_number && (
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {bannerOrder.shipment.tracking_number}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Bloque derecho - Botones (mobile: fila nueva debajo del contenido) */}
            <div className="flex w-full flex-wrap items-center gap-3 pt-3 border-t border-green-200/50 dark:border-green-800/30 lg:w-auto lg:flex-nowrap lg:flex-shrink-0 lg:gap-4 lg:pt-0 lg:border-t-0">
              {/* Botones de acción */}
              {bannerOrder && (
                <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto lg:flex-nowrap">
                  {/* Botón reimprimir etiqueta */}
                  <button
                    onClick={() => printOrder(bannerOrder.id)}
                    disabled={isOrderLoading(bannerOrder.id)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Reimprimir etiqueta"
                  >
                    {isOrderLoading(bannerOrder.id) ? (
                      <div className="w-4 h-4 mr-2 border border-current border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                        />
                      </svg>
                    )}
                    Reimprimir
                  </button>

                  {/* Botón cambiar bultos — PUT /orders/:id/change-packages */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2 border-warning/40 text-warning hover:bg-bg-warning/25"
                    data-testid="change-packages-open-button"
                    title={t('modals.adjust_packages.title')}
                    onClick={() => changePackagesModalRef.current?.openModal()}
                  >
                    <FontAwesomeIcon icon={faBoxesStacked} className="w-3.5 h-3.5" />
                    {t('actions.packages_short')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Información del pedido seleccionado - Ancho completo */}
      {isPackingMode && selectedOrder && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200/50 dark:border-blue-800/30 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center space-x-4 min-w-0">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                    Empaquetando
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{selectedOrder.ref}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {selectedOrder.customer?.name} • {selectedOrder.shop?.name}
                </p>
                {orderHasAttachments(selectedOrder) && (
                  <p className="text-sm text-info mt-2" data-testid="order-attachments-print-notice">
                    {t('alerts.attachmentsToPrint', { count: selectedOrderAttachmentCount })}
                  </p>
                )}
              </div>
              {/* Courier */}
              {selectedOrder.shipment?.courier && (
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-black/10 dark:bg-white/5 border border-white/10 flex items-center justify-center">
                    {selectedOrder.shipment.courier.image_hash ? (
                      <img
                        src={selectedOrder.shipment.courier.image_hash}
                        alt={selectedOrder.shipment.courier.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                        {selectedOrder.shipment.courier.name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-md font-bold text-gray-900 dark:text-white">
                      {selectedOrder.shipment.courier.name}
                    </span>
                    {selectedOrder.shipment.tracking_number && (
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {selectedOrder.shipment.tracking_number}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 flex-shrink-0">
              <button
                onClick={exitPackingMode}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors p-2 rounded-full hover:bg-white/50 dark:hover:bg-gray-800/50"
                title="Salir del modo empaquetado"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje de error de API - Simple y amigable */}
      {/* Los errores se muestran en toast (sin alertas bloqueantes) */}

      {/* Layout móvil - Una sola columna */}
      <div className="flex flex-col lg:hidden flex-1 min-h-0">
        {/* Scanner */}
        <div className="flex-shrink-0 mb-4">
          <ScannerInput
            scanPhase={scanPhase}
            stepDescription={scanStepDescription}
            onScan={handleScan}
            disabled={isScannerDisabled}
            disabledReason={getDisabledReason()}
          />
        </div>

        {/* Contenido principal */}
        <div className="flex-1 overflow-y-auto">
          {isPackingMode && selectedOrder ? (
            <div className="space-y-6">
              <div className="mb-1">
                <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                  <span className="text-blue-600 dark:text-blue-400">Productos del pedido {selectedOrder.ref}</span>
                  <span className="text-sm text-muted-foreground">({selectedOrder.lines?.length || 0} líneas)</span>
                </h2>
              </div>
              <OrderProductsList
                order={selectedOrder}
                assignedQuantities={assignedQuantities}
                assignedPackagingQuantities={assignedPackagingQuantities}
                assignedPackagingScanDetails={assignedPackagingScanDetails}
                packagings={packagings}
                preparation={preparation}
                activeBatchIdByProduct={activeBatchIdByProduct}
                onActiveBatchChange={setActiveBatchIdForProduct}
              />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="-mx-1 px-1">
                <OrdersList orders={orders || []} loading={ordersLoading} error={ordersError} sortConfig={sortConfig} />
              </div>
              <ProductsList
                lines={preparation.lines}
                orders={orders}
                isPreparationCompleted={isPreparationCompleted}
                preparationId={preparation.id}
                linePicks={preparation.line_picks}
              />
            </div>
          )}
        </div>
      </div>

      {/* Layout desktop - Dos columnas con separador vertical */}
      <div className="hidden lg:flex flex-1 items-start min-h-0">
        {/* Left Panel - Scanner + Pedidos (solo cuando NO hay pedido seleccionado) */}
        <div className="flex flex-col flex-1 pr-8 self-start">
          {/* Scanner */}
          <div className="flex-shrink-0">
            <ScannerInput
              scanPhase={scanPhase}
              stepDescription={scanStepDescription}
              onScan={handleScan}
              disabled={isScannerDisabled}
              disabledReason={getDisabledReason()}
            />
          </div>

          {/* Lista de pedidos (solo cuando NO hay pedido seleccionado) */}
          {!isPackingMode && (
            <div className="mt-6 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                  <span className="text-blue-600 dark:text-blue-400">Pedidos</span>
                  <span className="text-sm text-muted-foreground">({orders?.length || 0})</span>
                </h2>
                <OrderSortControls sortConfig={sortConfig} onSortChange={setSortConfig} />
              </div>
              <div className="pr-1">
                <OrdersList orders={orders || []} loading={ordersLoading} error={ordersError} sortConfig={sortConfig} />
              </div>
            </div>
          )}
        </div>

        {/* Separador vertical */}
        <div className="w-px bg-border flex-shrink-0"></div>

        {/* Right Panel - Productos de la preparación (modo normal) o Productos del pedido (modo empaquetado) */}
        <div className="flex flex-col flex-1 pl-8 min-h-0">
          <div className="flex flex-col h-full">
            {isPackingMode && selectedOrder ? (
              /* Modo empaquetado: mostrar productos del pedido seleccionado */
              <>
                <div className="flex-1 overflow-y-auto space-y-6">
                  <div className="mb-1">
                    <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                      <span className="text-blue-600 dark:text-blue-400">Productos del pedido {selectedOrder.ref}</span>
                      <span className="text-sm text-muted-foreground">({selectedOrder.lines?.length || 0} líneas)</span>
                    </h2>
                  </div>
                  <OrderProductsList
                    order={selectedOrder}
                    assignedQuantities={assignedQuantities}
                    assignedPackagingQuantities={assignedPackagingQuantities}
                    packagings={packagings}
                    preparation={preparation}
                    activeBatchIdByProduct={activeBatchIdByProduct}
                    onActiveBatchChange={setActiveBatchIdForProduct}
                  />
                </div>
              </>
            ) : (
              /* Modo normal: mostrar productos de la preparación */
              <div className="flex-1 overflow-hidden">
                <ProductsList
                  lines={preparation.lines}
                  orders={orders}
                  isPreparationCompleted={isPreparationCompleted}
                  preparationId={preparation.id}
                  linePicks={preparation.line_picks}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal cambio de bultos (pedido del banner) - Change packages modal */}
      {bannerOrder ? (
        <ChangePackagesModal ref={changePackagesModalRef} order={bannerOrder} onSuccess={refreshOrders} />
      ) : null}
    </div>
  );
};
